import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"

type LeadDenyDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (reason: string) => void
  isPending: boolean
  userLabel: string
  motivation?: string | null
}

/**
 * Turning down one of your users' budget requests.
 *
 * Same rule as the admin surface, deliberately: approving is a one-click
 * inline act, denying opens a dialog and REQUIRES a reason even though the API
 * accepts an empty one. A lead denies a colleague they see every day — "no"
 * without a sentence is the worst possible version of this interaction.
 */
export function LeadDenyDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  userLabel,
  motivation,
}: LeadDenyDialogProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState("")

  useEffect(() => {
    if (open) setReason("")
  }, [open])

  const trimmed = reason.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("localAiLead.requests.denyTitle", {
              user: userLabel,
              defaultValue: "Turn down {{user}}'s request",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("localAiLead.requests.denyDescription", {
              defaultValue: "They keep their current monthly budget. Your reason is shown to them.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {motivation && (
            <blockquote className="text-muted-foreground border-warning border-l-2 pl-3 text-sm italic">
              {motivation}
            </blockquote>
          )}
          <div className="space-y-2">
            <Label htmlFor="local-ai-lead-deny-reason">
              {t("localAiLead.requests.denyReasonLabel", { defaultValue: "Reason" })}
            </Label>
            <Textarea
              id="local-ai-lead-deny-reason"
              value={reason}
              placeholder={t("localAiLead.requests.denyReasonPlaceholder", {
                defaultValue: "Tell them why, so they know what to do next.",
              })}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button className="h-10" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            className="h-10"
            data-testid="local-ai-lead-deny-confirm"
            disabled={isPending || !trimmed}
            variant="destructive"
            onClick={() => onSubmit(trimmed)}
          >
            {isPending ? <Spinner className="mr-2 size-4" /> : null}
            {t("localAiLead.requests.denyConfirm", { defaultValue: "Turn down request" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
